import { useNotification } from 'admin';
import {
  EvidenceTypeEnum,
  useApiEvidenceV1BulkDeleteCreate,
  useApiEvidenceV1BulkUpdateSiteMaterialPartialUpdate,
} from 'api';
import { EvidenceTypeSelector, Form, type InferType, useForm, yup, yupResolver } from 'form';
import { ConfirmationButton, Stack } from 'ui';

import { TradeCompanySiteMaterialPicker } from '../../../site-material';

const schema = yup
  .object()
  .shape({
    evidence_type: yup.string().oneOf(Object.values(EvidenceTypeEnum)),
    trade_company: yup.string(),
    trade_company_site_material: yup.string(),
  })
  .test(
    'evidence_type-or-trade_company_site_material',
    'Either evidence type or site material must be provided',
    function (values) {
      if (!values.evidence_type && !values.trade_company_site_material) {
        return this.createError({
          message: 'Either evidence type or site material must be provided',
          path: 'evidence_type',
        });
      }
      return true;
    }
  );

type FormValues = InferType<typeof schema>;
export const EvidenceEditForm = ({
  selectedEvidenceIds,
  onSuccess,
}: {
  selectedEvidenceIds: number[];
  onSuccess?: () => void;
}) => {
  const { open } = useNotification();
  const form = useForm({
    resolver: yupResolver(schema),
  });

  const { mutate: updateEvidence } = useApiEvidenceV1BulkUpdateSiteMaterialPartialUpdate({
    mutation: {
      onError() {
        open?.({
          message: 'Error updating evidence',
          type: 'error',
        });
      },
      onSuccess() {
        open?.({
          message: 'Evidence updated',
          type: 'success',
        });
        onSuccess?.();
      },
    },
  });
  const { mutate: deleteEvidence } = useApiEvidenceV1BulkDeleteCreate({
    mutation: {
      onError() {
        open?.({
          message: 'Error deleting evidence',
          type: 'error',
        });
      },
      onSuccess() {
        open?.({
          message: 'Evidence deleted',
          type: 'success',
        });
        onSuccess?.();
      },
    },
  });

  const handleDelete = () => {
    deleteEvidence({
      data: {
        evidence_ids: selectedEvidenceIds,
      },
    });
  };

  const handleEdit = (data: FormValues) => {
    updateEvidence({
      data: {
        evidence_ids: selectedEvidenceIds,
        evidence_type: data.evidence_type,
        parent_id: data.trade_company_site_material ? Number.parseInt(data.trade_company_site_material) : undefined,
      },
    });
  };

  return (
    <Form {...form}>
      <Stack direction="row" alignItems="flex-end" gap={2}>
        <EvidenceTypeSelector
          label="New Evidence Type"
          name="evidence_type"
          required
          getOptionDisabled={(option) => option.value === EvidenceTypeEnum.BP}
        />
        <TradeCompanySiteMaterialPicker />
      </Stack>
      <Stack direction="row" alignItems="flex-end" justifyContent="flex-end" gap={2}>
        <ConfirmationButton
          disabled={selectedEvidenceIds.length === 0}
          variant="secondary"
          title="Warning!"
          message="You are about to delete this evidence from this site material. This will remove it from the associated quality report. Please confirm to continue."
          onConfirm={handleDelete}
        >
          Delete ({selectedEvidenceIds.length})
        </ConfirmationButton>

        <ConfirmationButton
          disabled={selectedEvidenceIds.length === 0}
          variant="primary"
          title="Warning!"
          message="You are about to change this evidence. This will remove it from the associated quality report. Please confirm to continue."
          onConfirm={form.handleSubmit(handleEdit)}
        >
          Edit ({selectedEvidenceIds.length})
        </ConfirmationButton>
      </Stack>
    </Form>
  );
};
